// Dev sign-in (auth.provider "fake" on the server): "Sign in" picks a local account id
// (`did:privy:dev-<random>`, kept in localStorage `ballio.auth.dev`) and mints fake tokens with
// @ballio/shared encodeFakeAuthToken. No secret and no modal: the server accepts these only in
// dev and with the fake settlement.
//
// The account is not fetched on construction: AccountSession (auth/session.ts) calls
// `refreshAccount()` once the module is up and signed in.

import { encodeFakeAuthToken } from '@ballio/shared';
import { fetchAccountMe, linkWallet, type AccountHttpOptions } from '../net/account-api.js';
import {
  defaultStorage,
  readKey,
  removeKey,
  type StorageLike,
  writeKey,
} from '../settings/store.js';
import { signal } from '../ui/dom.js';
import type { AuthModule, AuthStatus, AuthUser } from './auth.js';

export const FAKE_AUTH_STORAGE_KEY = 'ballio.auth.dev';
const TOKEN_TTL_SEC = 3600;

export interface FakeAuthDeps {
  readonly http?: AccountHttpOptions;
  readonly storage?: StorageLike | null;
  readonly nowMs?: () => number;
  readonly random?: () => number;
}

function newDevId(random: () => number): string {
  let s = '';
  for (let i = 0; i < 12; i++) s += Math.floor(random() * 36).toString(36);
  return `did:privy:dev-${s}`;
}

/** A fake wallet address derived from the id (stable per dev account; display only). */
export function fakeWalletFor(accountId: string): string {
  let h = 2166136261;
  for (let i = 0; i < accountId.length; i++) h = Math.imul(h ^ accountId.charCodeAt(i), 16777619);
  return `DevWa11et${(h >>> 0).toString(36)}`;
}

export function createFakeAuth(deps: FakeAuthDeps = {}): AuthModule {
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  const nowMs = deps.nowMs ?? Date.now;
  const random = deps.random ?? Math.random;
  const status = signal<AuthStatus>('signedOut');
  const user = signal<AuthUser | null>(null);
  let accountId: string | null = readKey(FAKE_AUTH_STORAGE_KEY, storage);
  if (accountId !== null && !/^did:privy:dev-[0-9a-z]{4,32}$/.test(accountId)) accountId = null;

  const mint = (kind: 'access' | 'identity'): string | null => {
    if (accountId === null) return null;
    const exp = Math.floor(nowMs() / 1000) + TOKEN_TTL_SEC;
    return encodeFakeAuthToken(
      kind === 'access'
        ? { kind, sub: accountId, exp }
        : { kind, sub: accountId, exp, wallet: fakeWalletFor(accountId) },
    );
  };

  const refreshAccount = async (): Promise<void> => {
    const token = mint('access');
    if (token === null || accountId === null) return;
    const id = accountId;
    let me = await fetchAccountMe(token, deps.http);
    if (me.status === 'ok' && me.account.wallet === null) {
      const idToken = mint('identity');
      if (idToken !== null) {
        const linked = await linkWallet(token, idToken, deps.http);
        if (linked.status === 'ok') me = linked;
      }
    }
    user.set(
      me.status === 'ok'
        ? {
            accountId: me.account.accountId,
            username: me.account.username,
            wallet: me.account.wallet,
            walletKind: me.account.wallet === null ? null : 'embedded',
            walletProblem:
              me.account.wallet !== null && (me.account.walletUnlinkedAt ?? null) !== null
                ? 'unlinked'
                : null,
            email: null,
            bannedUntil: me.account.bannedUntil,
          }
        : { accountId: id, username: null, wallet: null, email: null },
    );
  };

  if (accountId !== null) {
    status.set('signedIn');
    user.set({ accountId, username: null, wallet: null, email: null });
  }

  return {
    provider: 'fake',
    status,
    user,
    async signIn(): Promise<boolean> {
      if (accountId === null) {
        accountId = newDevId(random);
        writeKey(FAKE_AUTH_STORAGE_KEY, accountId, storage);
      }
      status.set('signedIn');
      await refreshAccount();
      return true;
    },
    signOut(): Promise<void> {
      accountId = null;
      removeKey(FAKE_AUTH_STORAGE_KEY, storage);
      user.set(null);
      status.set('signedOut');
      return Promise.resolve();
    },
    getAccessToken: () => Promise.resolve(mint('access')),
    getIdentityToken: () => Promise.resolve(mint('identity')),
    refreshAccount,
  };
}
